import { sendBI, sendDashBI, sendPartnersBI } from '../../services/BIService';
import { BIEvents, logoActionTypes } from '../../consts/bi';
import {
  getInviteById,
  getInviteRoles,
  getUserOrInviteById
} from '../selectors/teamMembers';
import { getTeamImageUrl } from '../selectors/teamDetails';
import { getContext, getMsid } from '../selectors/config';
import { getStatusFilter } from '../selectors/teamMembersTable';
import { InviteData } from '../../types/teamMembers';
import { getRoleNumOfScopes } from '../selectors/roles';
import { getIsCustomRole, getRoleScopes } from '../selectors/roleAreas';

export const reportDeleteTeamMemberConfirmed = (id: string) => (dispatch) => {
  dispatch(reportRemoveMemberBI(id, BIEvents.deleteTeamMemberClicked));
};

export const reportOpenDeleteTeamMemberDialog = (id: string) => (dispatch) => {
  dispatch(reportRemoveMemberBI(id, BIEvents.openDeleteTeamMemberDialog));
};

export const reportTeammatesPageLoad = (path: string) => (dispatch) => {
  dispatch(reportTeammatesPageLoadBI(path));
};

export const reportTeammatesPageFilterByStatus = () => (dispatch) => {
  dispatch(reportTeammatesPageFilterByStatusBI());
};

export const reportTeammatesPageFilterByRole = (numberOfRoles: number) => (dispatch) => {
  dispatch(reportTeammatesPageFilterByRoleBI(numberOfRoles));
};

export const reportOpenChangeRoles = (teammateId: string) => (dispatch) => {
  dispatch(reportOpenChangeRolesBI(teammateId));
};

export const reportUpdateRoles = (teammateId: string, newRoleIds: string[]) => (dispatch) => {
  dispatch(reportUpdateRolesBI(teammateId, newRoleIds));
};

export const reportOpenEditTeamProfileDialog = () => (dispatch) => {
  dispatch(reportOpenEditTeamProfileDialogBI());
};

export const reportResendInviteClick = (teammateId: string) => (dispatch) => {
  dispatch(reportResendInviteClickBI(teammateId));
};

export const reportDeleteRoleClick = (roleId: string) => (dispatch) => {
  dispatch(reportDeleteRoleBI(BIEvents.deleteCustomRoleClicked, roleId));
};

export const reportDeleteRoleSuccess = (roleId: string) => (dispatch) => {
  dispatch(reportDeleteRoleBI(BIEvents.deleteCustomRoleSuccess, roleId));
};

type cannotDeleteRoleAction = 'view teammates' | 'cancel';

export const reportCannotDeleteRole = (roleId: string, action: cannotDeleteRoleAction) => (dispatch) => {
  dispatch(reportDeleteRoleBI(BIEvents.cannotDeleteCustomRole, roleId, action));
};

export const reportInvitePeopleClick = (path: string, shouldUpgradeToInvite?: boolean) => (dispatch) => {
  dispatch(reportInvitePeopleClickBI(path, shouldUpgradeToInvite));
};

export const inviteEmailsError = (errorMessage: string) => (dispatch) => {
  dispatch(inviteEmailsErrorBI(errorMessage));
};

export const reportUpgradeButtonClick = (path: string) => (dispatch) => {
  dispatch(reportUpgradeButtonClickBI(path));
};

export const reportUpgradeModalLoad = (path: string) => (dispatch) => {
  dispatch(reportUpgradeModalLoadBI(path));
};

export const reportInvitePeopleLoad = (path: string) => (dispatch) => {
  dispatch(reportInvitePeopleLoadBI(path));
};

export const reportSendInviteClick = (roleIds: string[], path: string, numberOfValidEmails: number) => (dispatch) => {
  dispatch(reportSendInviteClickBI(roleIds, path, numberOfValidEmails));
};

export const reportManageRolesClick = (path: string) => (dispatch) => {
  dispatch(reportManageRolesClickBI(path));
};

export const reportManageRolesLoad = (path: string) => (dispatch) => {
  dispatch(reportManageRolesLoadBI(path));
};

export const reportViewRoleClick = (roleId: string) => (dispatch) => {
  dispatch(reportViewRoleClickBI(roleId));
};

export const reportViewOrEditRoleLoad = (mode: string, path: string, roleId: string) => (dispatch) => {
  dispatch(reportViewOrEditRoleLoadBI(mode, path, roleId));
};

export const reportDuplicateAndEditClick = (path: string, roleId: string) => (dispatch) => {
  dispatch(reportDuplicateAndEditClickBI(path, roleId));
};

export const reportOpenEditTeamDetailsModal = () => {
  return sendBI({
    evid: BIEvents.openEditTeamDetailsDialog,
  });
};

export const reportGetLinkClicked = (inviteId: string) => (dispatch) => {
  dispatch(reportGetLinkClickedBI(inviteId));
};

export const reportEditCustomRole = (roleId: string) => (dispatch) => {
  dispatch(reportEditCustomRoleBI(roleId));
};

export const reportUpdateCustomRole = (numOfScopes: string[], roleId: string, scopes: string[]) => (dispatch) => {
  dispatch(reportUpdateCustomRoleBI(numOfScopes, roleId, scopes));
};

export const reportCreateCustomRole = (numOfScopes: string[], path: string, scopes: string[], roleId: string, isDuplicate: boolean) => (dispatch) => {
  dispatch(reportCreateCustomRoleBI(numOfScopes, path, scopes, roleId, isDuplicate));
};

export const reportCreateCustomRoleClick = (path: string) => (dispatch) => {
  dispatch(reportCreateCustomRoleClickBI(path));
};

export const reportSetUpTeamLoad = () => (dispatch) => {
  dispatch(reportSetUpTeamLoadBI());
};

export const reportUpdateLogoStatus = (action_type: logoActionTypes) => () => sendDashBI({
  evid: BIEvents.updateLogo,
  action_type
});

export const learnMoreClicked = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  return sendBI({
    evid: BIEvents.teamManagementLearnMoreClicked,
    msid,
    context,
    origin,
  });
};

export const reportSaveTeamDetails = (onboarding) => (dispatch, getState) => {
  const state = getState();
  const is_logo_shown = !!getTeamImageUrl(state);
  const is_valid_team_name = true;
  return sendBI({ evid: BIEvents.saveTeamDetails, is_valid_team_name, is_logo_shown, onboarding });
};

const reportRemoveMemberBI = (teammate_id: string, eventId: BIEvents) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const userOrInvite = getUserOrInviteById(getState(), teammate_id);
  const invite_status = getInviteStatus(userOrInvite as InviteData);
  sendBI({
    evid: eventId,
    context,
    msid,
    invite_status,
    role_ids: userOrInvite?.roles,
    teammate_id,
  });
};

const reportTeammatesPageLoadBI = (path: string) => (dispatch, getState) => {
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.teammatesPageLoad,
    context: getContext(getState()),
    msid,
    origin,
  });
};

const reportTeammatesPageFilterByStatusBI = () => (dispatch, getState) => {
  const msid = getMsid(getState());
  sendBI({
    evid: BIEvents.teammatesFilteredByStatus,
    context: getContext(getState()),
    msid,
    status_selected: getStatusFilter(getState()),
  });
};

const reportTeammatesPageFilterByRoleBI = (num_selected: number) => (dispatch, getState) => {
  const msid = getMsid(getState());
  sendBI({
    evid: BIEvents.teammatesFilteredByRole,
    context: getContext(getState()),
    msid,
    num_selected,
  });
};

const reportOpenChangeRolesBI = (teammate_id) => (dispatch, getState) => {
  const context = getContext(getState());
  const userOrInvite = getUserOrInviteById(getState(), teammate_id);
  const invite_status = getInviteStatus(userOrInvite as InviteData);
  const msid = getMsid(getState());
  sendBI({
    evid: BIEvents.changeTeammateRoleClicked,
    context,
    msid,
    current_role_ids: userOrInvite?.roles,
    invite_status,
    teammate_id,
  });
};

const reportUpdateRolesBI = (teammate_id, new_role_ids) => (dispatch, getState) => {
  const context = getContext(getState());
  const userOrInvite = getUserOrInviteById(getState(), teammate_id);
  const invite_status = getInviteStatus(userOrInvite as InviteData);
  const msid = getMsid(getState());
  sendBI({
    evid: BIEvents.updateTeammateRole,
    context,
    msid,
    invite_status,
    new_role_ids,
    prev_role_ids: userOrInvite.roles,
    teammate_id,
  });
};

const reportOpenEditTeamProfileDialogBI = () => (dispatch) => {
  sendBI({
    evid: BIEvents.openEditTeamProfileDialog,
  });
};

const reportResendInviteClickBI = (teammate_id: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const invite = getInviteById(getState(), teammate_id);
  sendBI({
    evid: BIEvents.resendInviteClicked,
    context,
    msid,
    invite_id: invite.id,
    invite_role_ids: invite.roles,
    invite_status: invite.status,
  });
};

const reportDeleteRoleBI = (biEvent: number, role_id: string, action?: cannotDeleteRoleAction) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const num_scopes = getRoleNumOfScopes(getState(), role_id);
  let event = {
    evid: biEvent,
    context,
    msid,
    num_scopes,
    role_id,
  };
  if (action) {
    event['action'] = action;
  }
  sendBI(event);
};

const reportInvitePeopleClickBI = (path: string, upgrade_required: boolean = false) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.invitePeopleClick,
    context,
    msid,
    origin,
    upgrade_required,
  });
};

const inviteEmailsErrorBI = (error_message: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  sendBI({
    evid: BIEvents.inviteEmailsError,
    context,
    msid,
    error_message,
  });
};

const reportUpgradeButtonClickBI = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.upgradeButtonClick,
    context,
    msid,
    origin,
  });
};

const reportUpgradeModalLoadBI = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.upgradeModalLoad,
    context,
    msid,
    origin,
  });
};

const reportInvitePeopleLoadBI = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.invitePeopleLoad,
    context,
    msid,
    origin,
  });
};

const reportSendInviteClickBI = (invite_role_ids: string[], path: string, number_of_valid_emails: number) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  sendBI({
    evid: BIEvents.sendInviteClick,
    context,
    msid,
    invite_role_ids,
    origin,
    number_of_valid_emails,
  });
};

const reportGetLinkClickedBI = (invite_id: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const invite_role_ids = getInviteRoles(getState(), invite_id);
  return sendBI({
    evid: BIEvents.getInviteLink,
    context,
    msid,
    invite_id,
    invite_role_ids,
  });
};

const reportManageRolesClickBI = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  const origin = getBiOriginFromPath(path);
  return sendBI({
    evid: BIEvents.manageRolesClick,
    context,
    origin,
    msid,
  });
};

const reportManageRolesLoadBI = (path) => (dispatch, getState) => {
  const context = getContext(getState());
  const origin = getBiOriginFromPath(path);
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.manageRolesLoad,
    context,
    origin,
    msid,
  });
};

const reportViewRoleClickBI = (role_id: string) => (dispatch, getState) => {
  const context =  getContext(getState());
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.viewRoleClick,
    context,
    msid,
    role_id,
  });
};

const reportViewOrEditRoleLoadBI = (mode: string, path: string, role_id: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const origin = getBiOriginFromPath(path);
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.viewOrEditRoleLoad,
    context,
    msid,
    mode,
    origin,
    role_id,
  });
};

const reportDuplicateAndEditClickBI = (path: string, role_id: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const isCustom = getIsCustomRole(getState(), role_id);
  const origin = getBiOriginFromPath(path);
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.duplicateAndEditClick,
    context,
    msid,
    origin,
    role_id,
    role_type: isCustom ? 'custom' : 'predefined'
  });
};

const reportEditCustomRoleBI = (role_id: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.editCustomRole,
    context,
    msid,
    role_id,
  });
};

const reportUpdateCustomRoleBI = (num_scopes: string[], role_id: string, new_scope_ids: string[]) => (dispatch, getState) => {
  const context = getContext(getState());
  const old_scope_ids = getRoleScopes(getState(), role_id);
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.updateCustomRole,
    context,
    msid,
    num_scopes,
    role_id,
    new_scope_ids,
    old_scope_ids,
  });
};

const reportCreateCustomRoleBI = (num_scopes: string[], path: string, custom_role_scopes: string[], role_id: string, isDuplicate: boolean) => (dispatch, getState) => {
  const context = getContext(getState());
  const origin = `${isDuplicate ? 'duplicate - ' : ''}${getBiOriginFromPath(path)}`;
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.saveCustomRole,
    context,
    msid,
    num_scopes,
    origin,
    role_id,
    custom_role_scopes,
  });
};

const reportCreateCustomRoleClickBI = (path: string) => (dispatch, getState) => {
  const context = getContext(getState());
  const origin = getBiOriginFromPath(path);
  const msid = getMsid(getState());
  return sendBI({
    evid: BIEvents.createCustomRoleClick,
    context,
    msid,
    origin,
  });
};

const reportSetUpTeamLoadBI = () => (dispatch, getState) => {
  return sendPartnersBI({
    evid: BIEvents.setupTeamLoad,
    src: 80,
    tab_name: 'Settings',
    sub_tab: 'Team Management',
  });
};

const getBiOriginFromPath = (path: string = '') => {
  if (path?.includes('/rolesFilter')) {
    return BI_ORIGINS.TEAMMATES_PAGE_ROLES_FILTER;
  } else if (path?.includes('/welcome')) {
    return BI_ORIGINS.WELCOME_PAGE;
  } else if (path?.includes('/editTeamProfile')) {
    return BI_ORIGINS.EDIT_TEAM_PROFILE_MODAL;
  } else if (path?.includes('/invite')) {
    return BI_ORIGINS.INVITE_PEOPLE_PAGE;
  } else if (path?.includes('/edit')) {
    return BI_ORIGINS.EDIT_ROLES_PAGE;
  } else if (path?.includes('/roles')) {
    return BI_ORIGINS.ROLES_LIST_PAGE;
  } else if (path?.includes('/role')) {
    return BI_ORIGINS.ROLE_PAGE;
  } else if (path?.includes('/load')) {
    return BI_ORIGINS.LOAD;
  } else if (path === '/') {
    return BI_ORIGINS.TEAMMATES_PAGE;
  } else {
    return path;
  }
};

export const enum BI_ORIGINS {
  LOAD = 'team management page load',
  TEAMMATES_PAGE = 'team management page',
  TEAMMATES_PAGE_ROLES_FILTER = 'teammates page roles filter',
  WELCOME_PAGE = 'set up team',
  EDIT_TEAM_PROFILE_MODAL = 'edit team profile',
  INVITE_PEOPLE_PAGE = 'invite people page',
  EDIT_ROLES_PAGE = 'edit roles page',
  ROLES_LIST_PAGE = 'roles list page',
  ROLE_PAGE = 'role page',
}

const getInviteStatus = (userOrInvite: InviteData) => {
  return (userOrInvite as InviteData)?.status ?? 'accepted';
};
